import { X } from "@phosphor-icons/react";

// Unified banner component used for all in-app broadcasts so every row
// looks and behaves the same. Pick a variant and provide an icon — that's it.
//
// Variants map to a left-edge accent color + bg + border + icon tint:
//   celebration — pink/amber sunset      (birthdays, anniversaries)
//   announcement — FB blue               (team blasts)
//   managers    — amber                  (managers-only broadcasts)
//   alert       — red pulse              (missed readings, overdue)
//   success     — emerald                (celebration complete, goal hit)
//
// `onDismiss` is optional and renders an X on the right. `onClick` makes the
// whole row a button (used by the missed-reading banner → scrolls to entry).
const VARIANTS = {
  celebration: {
    bg: "bg-gradient-to-r from-pink-50 to-amber-50",
    accent: "bg-pink-400",
    border: "border-pink-200",
    icon: "text-pink-600",
    title: "text-pink-900",
    body: "text-pink-800/90",
  },
  announcement: {
    bg: "bg-blue-50",
    accent: "bg-blue-500",
    border: "border-blue-200",
    icon: "text-blue-600",
    title: "text-blue-900",
    body: "text-blue-800/90",
  },
  managers: {
    bg: "bg-amber-50",
    accent: "bg-amber-500",
    border: "border-amber-200",
    icon: "text-amber-600",
    title: "text-amber-900",
    body: "text-amber-800/90",
  },
  alert: {
    bg: "bg-gradient-to-r from-rose-50 to-red-50",
    accent: "bg-rose-500 animate-pulse",
    border: "border-rose-300",
    icon: "text-rose-600",
    title: "text-rose-900",
    body: "text-rose-800/90",
  },
  success: {
    bg: "bg-emerald-50",
    accent: "bg-emerald-500",
    border: "border-emerald-200",
    icon: "text-emerald-600",
    title: "text-emerald-900",
    body: "text-emerald-800/90",
  },
};

export default function Banner({
  variant = "announcement",
  icon: Icon,
  title,
  children,
  onDismiss,
  onClick,
  trailing,
  leading,
  testid,
}) {
  const v = VARIANTS[variant] || VARIANTS.announcement;
  const Wrapper = onClick ? "button" : "div";
  return (
    <Wrapper
      onClick={onClick}
      className={`relative w-full text-left overflow-hidden rounded-xl border ${v.bg} ${v.border} shadow-sm transition-all ${onClick ? "hover:shadow-md active:scale-[0.99]" : ""}`}
      data-testid={testid || `banner-${variant}`}
    >
      {/* Left edge accent strip — the unifying design signature. */}
      <span className={`absolute inset-y-0 left-0 w-1 ${v.accent}`} aria-hidden />
      <div className="relative flex items-start gap-3 px-3.5 py-2.5 pl-4">
        {leading ? (
          <div className="flex-shrink-0 mt-0.5">{leading}</div>
        ) : Icon ? (
          <div className={`flex-shrink-0 mt-0.5 ${v.icon}`}>
            <Icon weight="fill" className="w-4 h-4" />
          </div>
        ) : null}
        <div className="flex-1 min-w-0">
          {title && (
            <p
              className={`text-sm font-bold leading-tight ${v.title}`}
              style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}
            >
              {title}
            </p>
          )}
          {children && <div className={`text-xs leading-snug mt-0.5 ${v.body}`}>{children}</div>}
        </div>
        {trailing && <div className="flex-shrink-0 ml-2 self-center">{trailing}</div>}
        {onDismiss && (
          <button
            onClick={(e) => { e.stopPropagation(); onDismiss(); }}
            className="flex-shrink-0 ml-1 p-1 rounded hover:bg-black/5"
            aria-label="Dismiss"
          >
            <X weight="bold" className="w-3.5 h-3.5 text-slate-500" />
          </button>
        )}
      </div>
    </Wrapper>
  );
}
